import { NO_VIZ, USE_VIZ_P13N } from 'constants/productRecoTypes';
import marketplace from 'cfg/marketplace.json';
import { constructMSAImageUrl, indefiniteArticleSelector } from 'helpers';

const { pdp: { showVisualRecos } } = marketplace;

// a collection of pure helper functions for working with styles and stocks
const NON_NUMERIC_MATCHER = /[^\d^.]+/g;

// Since the API doesn't return the styles keyed by styleId, this helper function
// iterates through the array of styles and creates a dictionary
function getStyleMap(styles) {
  const styleMap = {};
  styles.forEach(style => {
    styleMap[style.styleId] = style;
  });
  return styleMap;
}

// Returns the corresponding style for the provided color.  If color is not provided or is no longer in stock, returns the first available style
function getStyleByColor(styles, colorId) {
  if (!colorId) {
    return styles[0];
  }

  for (const style of styles) {
    if (style.colorId === colorId) {
      return style;
    }
  }
  // if that colorId doesn't exist anymore, just return the first style
  return styles[0];
}

function hasMatchingDimensions(stock, selectedSizing) {
  return Object.keys(selectedSizing).every(dimId => stock[dimId] === selectedSizing[dimId]);
}

/**
 * Gets  the stock object for a given  color, and sizing combination.
 *
 * Returns null if there is no stock for that combination of colors and sizing, or if it is out of stock and includeOos is not set.
 *
 * @param stockData {Array} list of stock information for the product
 * @param colorId {String} color to match against
 * @param selectedSizing {Object} map of dimensionId -> dimensionValues represented the selected sizes.
 * @param includeOos {boolean=false} If true, will return stocks that have 0 onHand.
 */
function getStockBySize(stockData, colorId, selectedSizing, includeOos = false) {
  return stockData.find(stock => (stock.color === colorId && hasMatchingDimensions(stock, selectedSizing) && (includeOos || stock.onHand !== '0'))) || null;
}

/**
 * Gets  the stock object for a given  color, and sizing combination.
 *
 * Returns null if there is no stock for that combination of colors and sizing, or if it is out of stock and includeOos is not set.
 *
 * @param stockData {Array} List of stocks of currently selected selected
 * @param selectedSizing {Object} map of dimensionId -> dimensionValues represented the selected sizes.
 * @param includeOos {boolean=false} If true, will return stocks that have 0 onHand.
 */
function getSelectedStyleStockBySize(stockData = [], sizing, selectedSizing = {}) {
  const sizeName = getCurrentSelectedSizeName(sizing, selectedSizing);
  if (sizeName && stockData.length && Object.keys(selectedSizing).length) {
    return stockData.find(({ size }) => sizeName === size);
  }
  return null;
}

/**
 * Returns available colors for given sizing.
 *
 * @param stockData {Array} list of stock information for the product
 * @param selectedSizing {Object} map of dimensionId -> dimensionValues represented the selected sizes.
 */
function getColorsBySize(stockData, selectedSizing) {
  return stockData.filter(stock => hasMatchingDimensions(stock, selectedSizing)) || [];
}

function getPriceForProduct(styles) {
  let price;
  for (const style of styles) {
    if (!price) {
      ({ price } = style);
    } else if (price !== style.price) {
      return undefined;
    }
  }
  return price;
}

export function priceToFloat(price) {
  return parseFloat(price.replace(NON_NUMERIC_MATCHER, ''));
}

// can't actually trust onSale flag to mean price is lower than originalPrice
function isStyleOnSale(style) {
  return style && style.originalPrice ? priceToFloat(style.originalPrice) > priceToFloat(style.price) : false;
}

/**
 * Returns the payload for a legacy StockEventHC
 */
function getStockEventPayload(productId, styleId, colorId, sizing, selectedSizing, stock, inStock) {
  let payload = `${inStock ? 'IS' : 'OOS'}|p=${productId}&c=${colorId}&y=${styleId}&k=${stock ? stock.id : ''}`;
  for (const dimId of sizing.dimensionsSet) {
    payload += `&r=${selectedSizing[dimId]}`;
  }
  return payload;
}

/**
 * Returns the user friendly name for the first invalid dimension the user has selected.
 * @param selectedSizing {Object}  map of dimensionId to dimensionValue user has selected.
 * @param sizing {Object} sizing 2.0 data for product
 */
function getMissingDimensionName(selectedSizing, { dimensionsSet, dimensionIdToName }) {
  const missingDimId = dimensionsSet.find(dimId => !selectedSizing[dimId]);
  return dimensionIdToName[missingDimId];
}

function isSizeSelectionComplete(sizeData, selectedSizing) {
  return sizeData.dimensions.length === Object.keys(selectedSizing).length;
}
/**
 * Returns the percentage off of the original price the item is on sale for rounded to the nearest integer.
 * @param  {Object} style object with price and originalPrice
 * @return {Integer}       number representing the percentage off
 */
function getPercentOff({ originalPrice, price }) {
  return originalPrice && price ? Math.round((1 - priceToFloat(price) / priceToFloat(originalPrice)) * 100) : 0;
}

/**
 * Return sharing link for a given productId/colorId
 * @param productId
 * @param colorId (optional)
 * @returns {string}
 */
function getSharingButtonLink(productId, colorId) {
  const { links: { sharing: { baseUrl } } } = marketplace;
  return `${baseUrl}/product/${productId}${colorId ? `/color/${colorId}` : ''}`;
}

function isShoeType(productType) {
  return (productType && productType.toLowerCase() === 'shoes');
}

function isGiftCard(productType) {
  return (productType && productType.toLowerCase() === 'gift cards');
}

const RECO_NAME_TO_SOURCE = {
  'pd_detail_2': 'p13nvisual'
};

function translateRecoNameToAnalyticsSource(recoName) {
  if (recoName in RECO_NAME_TO_SOURCE) {
    return RECO_NAME_TO_SOURCE[recoName];
  }
  return 'p13n';

}

/**
   * Returns whether the visual P13N reco endpoint, or standard p13n  should be used for the recos on the current product.
   * @param  {String} productType          product type ('Clothing', 'Accessories', 'Shoes' etc)
   * @return {Symbol}                    The symbol USE_VIZ_P13N, or NO_VIZ
   */
function shouldUseVisualRecos(productType) {
  if (showVisualRecos && ProductUtils.isShoeType(productType)) {
    return USE_VIZ_P13N;
  }

  return NO_VIZ;
}

/**
 * Returns the Product URL representing the given asin.
 * @param {Object} Product object
 * @param {String} asin the asin to look for
 * @return {String} the product URL including productId and colorId for the given asin.  If matching asin is not found returns the product's default url
 */
function getProductUrlFromAsin({ defaultProductUrl, styles }, asin) {
  asin = asin && asin.toUpperCase();
  if (styles) {
    const style = styles.find(({ stocks }) => stocks.find(stock => stock.asin === asin));
    return (style && style.productUrl) || defaultProductUrl;
  }
}

/**
 * Returns the Amethyst reco widget type based of title
 * @param {String} title the title of the reco
 */
function translateRecoTitleToAmethystWidget(title) {
  if (title) {
    const reco = title.toLowerCase();
    const mapping = {
      'customers who viewed this item also viewed': 'CUSTOMERS_WHO_VIEWED_WIDGET',
      'customers who bought this item also bought': 'CUSTOMERS_WHO_BOUGHT_WIDGET',
      'similar styles you might like': 'SIMILAR_PRODUCT_WIDGET',
      'you may also like': 'YOU_MAY_ALSO_LIKE_WIDGET'
    };
    return mapping[reco] || 'UNKNOWN_RECOMMENDATION_WIDGET';
  }
  return null;
}

/*
  Returns whether the Product Page is loaded and ready to render.  Takes into account that detail data, image data, sizing data, the isLoading flag, and whether the requested productId matches the currently loaded product.
 */
function isProductDataLoaded({ selectedSizing, detail, isLoading }, { productId }) {
  return detail &&
    detail.productId === productId &&
    detail.styles && detail.styles.length && // if product is oos and loaded [from reviews], force the fetch to occur so natural oos cycle occurs.
    selectedSizing &&
    !isLoading;
}

/**
 * Get the number of reviews for the given product
 *
 * @param product        state.product
 * @param valueIfNoData  the value to return if the data is loading or missing
 *                       (defaults to 0)
 */
function getNumberOfReviews(product, valueIfNoData = 0) {
  if (!product) {
    return valueIfNoData;
  }
  const { reviewData } = product;
  if (!reviewData) {
    return valueIfNoData;
  }
  const { isLoading, reviews } = reviewData;
  if (isLoading || !reviews) {
    return valueIfNoData;
  }
  return reviews.length;
}

/**
 * For a given style return a list of complete MSA image URLs for each angle of the style in thumbnail size.
 *
 * TODO figure out why these are arrays of objects and not arrays of strings
 *
 * @param style
 * @return {Array} array of objects with filename property for the image src
 */
function buildAngleThumbnailImages(style, width) {
  if (!style || !style.images) {
    return [];
  }

  return style.images.map(({ imageId, type }) => ({
    filename: constructMSAImageUrl(imageId, { width }),
    imageId,
    type
  }));
}

// This function name will change if FPI test goes to treatment
// Filter products down to certain image angles documented here: https://github01.zappos.net/mweb/marty/issues/11695#issue-38224
function buildFewerAngleThumbnailImages(style, productType, gender, width) {
  if (!style || !style.images) {
    return [];
  }

  let filters;
  if (['kids', 'boys', 'girls'].includes(gender) && productType !== 'Shoes') {
    filters = ['MAIN'];
  } else {
    filters = function() {
      switch (productType) {
        case 'Shoes':
          return ['PAIR', 'TOPP', 'BOTT', 'LEFT', 'BACK', 'MAIN' /* Things like https://marty.zappos.com:9443/p/crocs-jibbitz-fire/product/9368915/color/18807 live under 'Shoes' */];
        case 'Bags':
          return ['MAIN', 'PT01', 'PT04'];
        case 'Clothing':
          return ['MAIN', 'PT02', 'PAIR' /* Socks live under 'Clothing'*/];
        case 'Accessories':
        case 'Eyewear':
        case 'Jewelry':
        case 'Watches':
          return ['MAIN'];
        default:
          return ['PAIR', 'TOPP', 'BOTT', 'LEFT', 'BACK', 'MAIN', 'RGHT', 'FRNT', 'PT01', 'PT02', 'PT03', 'PT04']; // Don't filter at all if the switch falls through
      }
    }();
  }

  return style.images
    .filter(({ type }) => filters.includes(type))
    .map(({ imageId, type }) => ({
      filename: constructMSAImageUrl(imageId, { width }),
      imageId,
      type
    }));

}

function buildSizeMessagingText(dimensionName) {
  return `Please select ${indefiniteArticleSelector(dimensionName)} ${dimensionName}`;
}

function getCurrentSelectedSizeName(sizeData, selectedSizing) {
  const { dimensionIdToName } = sizeData;
  const sizeKey = Object.keys(dimensionIdToName).find(key => dimensionIdToName[key] === 'size');
  const selectedSizeId = selectedSizing?.[sizeKey];
  if (selectedSizeId) {
    return sizeData.allValues.find(v => v.id === selectedSizeId)?.value;
  }
  return null;
}

function getGender(product = {}) {
  const { gender = '', genders = [] } = product;
  if (gender) {
    return gender.toLowerCase();
  }
  const lowercaseGenders = genders.map(s => s.toLowerCase());
  if (lowercaseGenders.length === 1) {
    return lowercaseGenders[0];
  } else if (lowercaseGenders.length === 2 && lowercaseGenders.includes('boys') && lowercaseGenders.includes('girls')) {
    return 'kids';
  }
  return 'general';
}

function hasAvailableStock(style) {
  return style.stocks.some(({ onHand }) => onHand !== '0');
}

const ProductUtils = {
  buildAngleThumbnailImages,
  buildFewerAngleThumbnailImages,
  buildSizeMessagingText,
  getColorsBySize,
  getCurrentSelectedSizeName,
  getSharingButtonLink,
  getMissingDimensionName,
  getNumberOfReviews,
  getPercentOff,
  getPriceForProduct,
  getGender,
  getProductUrlFromAsin,
  getStockBySize,
  getSelectedStyleStockBySize,
  getStockEventPayload,
  getStyleByColor,
  getStyleMap,
  hasAvailableStock,
  isGiftCard,
  isProductDataLoaded,
  isSizeSelectionComplete,
  isStyleOnSale,
  priceToFloat,
  isShoeType,
  shouldUseVisualRecos,
  translateRecoNameToAnalyticsSource,
  translateRecoTitleToAmethystWidget
};

export default ProductUtils;
